import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';
import { Instagram } from 'lucide-react';

const BLUE = '#47ABDB';

const SectionWrapper = styled.section`
  padding: 60px 20px;
  @media (max-width: 768px) {
    padding: 40px 15px;
  }
`;

const Inner = styled.div`
  max-width: 1700px;
  margin: 0 auto;
  text-align: center;
`;

const IconBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #4e4094, #14cbde);
  color: #fff;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  color: #111D4E;
  font-size: 2.4rem;
  margin: 0 0 .5rem;
  display: inline-block;
  padding-bottom: 8px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);
  background-size: 100% 3px;
  background-position: 0 bottom;
  background-repeat: no-repeat;
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const SectionSubtitle = styled.p`
  color: #5B6B78;
  font-size: 1.05rem;
  margin: 1rem 0 2rem;
`;

const FollowLink = styled.a`
  color: ${BLUE};
  font-weight: 700;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const Post = styled.a`
  position: relative;
  display: block;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border-radius: 18px;
  background: #eef2f7;
  box-shadow: 0 12px 32px rgba(15, 23, 42, .12);
`;

const PostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .4s ease;
  ${Post}:hover & {
    transform: scale(1.06);
  }
`;

const IgBadge = styled.div`
  position: absolute;
  top: .75rem;
  right: .75rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(17, 29, 78, .45);
  backdrop-filter: blur(2px);
  color: #fff;
`;

// Hidden by default on desktop, revealed on hover; on touch devices there's
// no hover, so it's always shown there (see the max-width block).
const CaptionOverlay = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  padding: 2.5rem 1.1rem .9rem;
  background: linear-gradient(to top, rgba(10, 15, 40, .92) 10%, rgba(10, 15, 40, 0) 100%);
  color: #fff;
  text-align: left;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity .25s ease, transform .25s ease;
  ${Post}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
  @media (max-width: 768px) {
    opacity: 1;
    transform: none;
    padding: 2rem .85rem .7rem;
  }
`;

const CaptionText = styled.p`
  margin: 0 0 .3rem;
  font-size: .85rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  @media (max-width: 768px) {
    -webkit-line-clamp: 2;
    font-size: .78rem;
  }
`;

const CaptionDate = styled.span`
  font-size: .72rem;
  opacity: .75;
  letter-spacing: .02em;
`;

// Public homepage section - reads the cached feed heartscms keeps in sync
// with Instagram, never Instagram itself. Renders nothing if the feature
// isn't enabled/configured yet or there's no cached content, so it's safe to
// leave wired into the homepage before the integration is fully set up.
const InstagramFeed = () => {
  const intl = useIntl();
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_API_URL}/api/cms/instagram/public/feed`)
      .then(({ data }) => { if (!cancelled) setFeed(data); })
      .catch(() => { if (!cancelled) setFeed(null); });
    return () => { cancelled = true; };
  }, []);

  if (!feed || !feed.enabled || !feed.items || feed.items.length === 0) {
    return null;
  }

  return (
    <SectionWrapper>
      <Inner>
        <IconBadge>
          <Instagram size={26} />
        </IconBadge>
        <div>
          <SectionTitle>
            <FormattedMessage id="instagramFeed.title" defaultMessage="Follow us on Instagram" />
          </SectionTitle>
        </div>
        <SectionSubtitle>
          {feed.profileUrl ? (
            <FollowLink href={feed.profileUrl} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="instagramFeed.follow" defaultMessage="View our profile" />
            </FollowLink>
          ) : (
            <FormattedMessage id="instagramFeed.subtitle" defaultMessage="A look at recent moments from HEARTS." />
          )}
        </SectionSubtitle>
        <Grid>
          {feed.items.map((item, index) => (
            <Post
              key={item.permalink || index}
              href={item.permalink}
              target="_blank"
              rel="noopener noreferrer"
              title={item.caption || undefined}
            >
              <PostImage
                src={item.displayUrl}
                alt={item.caption || intl.formatMessage({ id: 'instagramFeed.title', defaultMessage: 'Follow us on Instagram' })}
                loading="lazy"
              />
              <IgBadge>
                <Instagram size={16} />
              </IgBadge>
              {(item.caption || item.postedAt) && (
                <CaptionOverlay>
                  {item.caption && <CaptionText>{item.caption}</CaptionText>}
                  {item.postedAt && (
                    <CaptionDate>
                      {intl.formatDate(item.postedAt, { day: 'numeric', month: 'short' })}
                    </CaptionDate>
                  )}
                </CaptionOverlay>
              )}
            </Post>
          ))}
        </Grid>
      </Inner>
    </SectionWrapper>
  );
};

export default InstagramFeed;
